"use client";

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocale, formatDate } from "@/i18n/format";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { getStatusColor } from "./platform-colors";
import { PublishFailureDetails } from "./publish-failure-details";
import { useReschedulePost, useCancelPost, usePublishScheduledNow } from "@/hooks/use-calendar";
import { viewerTimeZone, instantToLocalParts, wallTimeToInstant } from "@content-os/scheduling";
import { MIN_SCHEDULE_LEAD_MS } from "@content-os/config";
import type { CalendarEvent } from "@content-os/types";
import { Calendar, X, Zap } from "lucide-react";
import { POST_TYPE_META, type PostType } from "@/hooks/use-post-generation";
import { MediaPreview, MediaLightbox, mediaItemsFromPiece } from "@/components/media";
import type { TFunction } from "i18next";

function detailLabel(event: CalendarEvent, t: TFunction): string {
  const pt = (event.piece?.postType ?? event.postType) as PostType | null | undefined;
  if (pt && POST_TYPE_META[pt]) {
    const meta = POST_TYPE_META[pt];
    return `${t(`platform.${meta.platform}`, { defaultValue: meta.platform })} ${meta.label}`;
  }
  return t(`platform.${event.platform}`, { defaultValue: event.platform });
}

interface PostDetailModalProps {
  event: CalendarEvent | null;
  isOpen: boolean;
  onClose: () => void;
  brandId: string;
  canSchedule: boolean;
}

export function PostDetailModal({
  event,
  isOpen,
  onClose,
  brandId,
  canSchedule,
}: PostDetailModalProps) {
  const { t } = useTranslation("calendar");
  const locale = useLocale();
  // Display and reschedule in the viewer's local timezone (see Calendar.tsx).
  const tz = viewerTimeZone();
  const [isRescheduling, setIsRescheduling] = useState(false);
  const [newDate, setNewDate] = useState("");
  const [newTime, setNewTime] = useState("");
  const [lbIndex, setLbIndex] = useState<number | null>(null);
  const rescheduleMut = useReschedulePost(brandId);
  const cancelMut = useCancelPost(brandId);
  const publishNowMut = usePublishScheduledNow(brandId);

  if (!event) return null;

  const items = mediaItemsFromPiece({
    id: event.id,
    mediaUrls: event.mediaUrls,
    thumbnailUrl: event.thumbnailUrl,
    format: (event.piece?.format ?? event.postType) as string | null,
    title: event.title,
  });

  const handleReschedule = () => {
    if (!newDate || !newTime) return;
    const [y, m, d] = newDate.split("-").map(Number);
    const [hh, mm] = newTime.split(":").map(Number);
    const instant = wallTimeToInstant(y, m, d, hh, mm, tz);
    if (!instant) return; // wall time falls in a DST spring-forward gap
    const scheduledFor = instant.toISOString();
    rescheduleMut.mutate(
      { postId: event.id, data: { scheduledFor } },
      {
        onSuccess: () => {
          setIsRescheduling(false);
          onClose();
        },
      },
    );
  };

  const handleCancel = () => {
    cancelMut.mutate(event.id, { onSuccess: onClose });
  };

  const handlePublishNow = () => {
    if (!confirm(t("postDetail.publishNowConfirm"))) return;
    publishNowMut.mutate(event.id, { onSuccess: onClose });
  };

  const scheduledDate = new Date(event.scheduledFor);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>{detailLabel(event, t)}</span>
            <span className="font-mono text-sm text-muted-foreground">
              · {event.title}
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Media preview */}
          {items[0] && (
            <MediaPreview
              item={items[0]}
              className="aspect-video w-full rounded-lg"
              onOpen={() => setLbIndex(0)}
              label={event.title}
            />
          )}
          <MediaLightbox
            items={items}
            index={lbIndex}
            onIndexChange={setLbIndex}
            onClose={() => setLbIndex(null)}
          />

          {/* Status */}
          <div className="flex items-center gap-2">
            <span
              className="h-2 w-2 rounded-full"
              style={{ backgroundColor: getStatusColor(event.status) }}
            />
            <span className="text-sm">
              {t(`status.${event.status}`, { defaultValue: event.status.charAt(0) + event.status.slice(1).toLowerCase() })}
            </span>
            {(event.retryCount ?? 0) > 0 && (
              <Badge variant="outline" className="text-xs">
                {t("postDetail.retries", { count: event.retryCount ?? 0 })}
              </Badge>
            )}
          </div>

          {(event.caption ?? event.piece?.caption) && (
            <div className="rounded-md bg-muted/50 p-2 text-sm whitespace-pre-wrap">
              {event.caption ?? event.piece?.caption}
            </div>
          )}

          <PublishFailureDetails failure={event} />

          {/* Metadata preview */}
          <div className="space-y-2 text-sm">
            {Object.entries(event.metadata ?? {}).map(([key, value]) => (
              <div key={key} className="flex justify-between">
                <span className="text-muted-foreground capitalize">
                  {t(`scheduleModal.fields.${key}`, { defaultValue: key.replace(/([A-Z])/g, " $1") })}
                </span>
                <span className="text-right max-w-[250px] truncate">
                  {typeof value === "string" ? value : JSON.stringify(value)}
                </span>
              </div>
            ))}
          </div>

          {/* Schedule info */}
          <div className="flex items-center justify-between border-t pt-3">
            <div>
              <div className="text-xs text-muted-foreground">{t("postDetail.scheduledFor")}</div>
              <div className="text-sm font-medium">
                {formatDate(scheduledDate, locale, { timeZone: tz })} {t("postDetail.at")}{" "}
                {formatDate(scheduledDate, locale, {
                  hour: "numeric",
                  minute: "2-digit",
                  timeZone: tz,
                })}
              </div>
            </div>
            {canSchedule && event.status === "SCHEDULED" && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsRescheduling(!isRescheduling)}
              >
                <Calendar size={14} className="mr-1" /> {t("postDetail.reschedule")}
              </Button>
            )}
          </div>

          {/* Reschedule form */}
          {isRescheduling && (
            <div className="flex items-center gap-2 border rounded-md p-2">
              <Input
                type="date"
                value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
                min={instantToLocalParts(new Date(Date.now() + MIN_SCHEDULE_LEAD_MS), tz).dateKey}
                className="h-8"
              />
              <Input
                type="time"
                value={newTime}
                onChange={(e) => setNewTime(e.target.value)}
                className="h-8"
              />
              <Button
                size="sm"
                onClick={handleReschedule}
                disabled={rescheduleMut.isPending}
              >
                {t("postDetail.save")}
              </Button>
            </div>
          )}

          {/* Project info */}
          {event.projectTitle && (
            <div className="text-xs text-muted-foreground">
              {t("postDetail.project")}{" "}
              <span className="text-foreground">{event.projectTitle}</span>
            </div>
          )}

          {/* Actions */}
          {canSchedule && event.status === "SCHEDULED" && (
            <div className="flex items-center justify-between border-t pt-3">
              <Button
                size="sm"
                onClick={handlePublishNow}
                disabled={publishNowMut.isPending}
              >
                <Zap size={14} className="mr-1" /> {t("postDetail.publishNow")}
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleCancel}
                disabled={cancelMut.isPending}
              >
                <X size={14} className="mr-1" /> {t("postDetail.cancelPost")}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
